import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Seo } from '../components/Seo.jsx';
import { Button, Card } from '../components/ui.jsx';

/** The public site, as an agent should be told it. Fixed so the prerendered page says the same. */
const SITE = 'https://affily.me';

/**
 * What to paste into an agent so it uses Affily properly.
 *
 * The prompt is short on purpose: the rules live in /agents.md, which is kept
 * in step with the API, so the agent is told to read that rather than given a
 * copy here that could drift.
 */
export const AGENT_PROMPT = `You can use Affily (${SITE}), a creator marketplace, for me.

Before doing anything, read ${SITE}/agents.md and follow its rules. In short:
- Reading programs and creators needs no key: ${SITE}/api/programs and ${SITE}/api/creators.
- To sign me up and build my profile, use ${SITE}/api/v1 (spec: ${SITE}/api/v1/openapi.json). Save the API key it returns; it is shown once.
- Everything you build is a draft. Never tell me it is live: tell me to sign in, connect a social account, and publish it myself.
- Never make up follower counts or other numbers. Ask me, or leave them blank.
- If a request is refused with a code like SOCIAL_REQUIRED or EMAIL_UNVERIFIED, tell me what I need to do.

What I want: `;

function CopyPrompt() {
  const [state, setState] = useState('idle');
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(AGENT_PROMPT);
      setState('copied');
    } catch {
      setState('failed');
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), 2500);
  }

  return (
    <Card className="mt-6 p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">Paste this into your agent</h2>
        <Button size="sm" variant="outline" onClick={copy}>
          {state === 'copied' ? 'Copied' : 'Copy'}
        </Button>
      </div>
      {state === 'failed' && (
        <p className="mt-2 text-xs text-negative" role="alert">
          Could not copy — select the text and copy it manually.
        </p>
      )}
      <pre className="mt-3 overflow-x-auto whitespace-pre-wrap rounded bg-paper-sunken p-3 text-xs leading-relaxed">
        {AGENT_PROMPT}
      </pre>
      <p className="mt-2 text-xs text-ink-muted">Add what you want done at the end, then send it.</p>
    </Card>
  );
}

export default function ForAgents() {
  return (
    <>
      <Seo
        title="Use Affily with your agent"
        description="A prompt to give Claude, ChatGPT or any agent so it can compare programs and build your Affily profile, and the rules it follows."
      />
      <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <h1 className="text-4xl font-semibold tracking-tight">Use Affily with your agent</h1>
        <p className="mt-3 text-ink-muted">
          An AI agent can compare programs for you, sign you up and fill in your profile. Give it the
          prompt below; it points the agent at instructions we keep up to date with the API.
        </p>

        <CopyPrompt />

        <section className="mt-12 grid gap-8 sm:grid-cols-2">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">What an agent can do</h2>
            <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-ink-muted">
              <li>Compare every public program’s terms and tracked conversion rates, with no key.</li>
              <li>Sign you up, as a creator or a brand.</li>
              <li>Upload your photos and videos, and write your profile and rates.</li>
            </ul>
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">What stays with you</h2>
            <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-ink-muted">
              <li>Connecting your TikTok, Instagram, YouTube or Facebook Page.</li>
              <li>Publishing your profile. Until you do, nobody sees it.</li>
              <li>Applying to programs, and anything to do with money or your password.</li>
            </ul>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="text-lg font-semibold tracking-tight">Why it works this way</h2>
          <p className="mt-3 text-sm text-ink-muted">
            The key an agent gets when it signs you up can build a draft profile and nothing more.
            It only works on the agent API, so a leaked key cannot read your earnings, create
            programs or take over your account. Numbers an agent types in are labelled
            self-reported; verified ones come only from an account you connect yourself.
          </p>
          <p className="mt-3 text-sm text-ink-muted">
            For developers: the full instructions are at{' '}
            <a href="/agents.md" className="text-accent hover:underline">
              /agents.md
            </a>
            , the spec at{' '}
            <a href="/api/v1/openapi.json" className="text-accent hover:underline">
              /api/v1/openapi.json
            </a>
            , and an index for LLMs at{' '}
            <a href="/llms.txt" className="text-accent hover:underline">
              /llms.txt
            </a>
            . Keys you mint yourself are on your{' '}
            <Link to="/dashboard/keys" className="text-accent hover:underline">
              API keys page
            </Link>
            .
          </p>
        </section>
      </article>
    </>
  );
}
